import { StateCreator } from "zustand"
import { createQuiz, deleteQuiz, getQuizzes, reorderQuiz, ReorderQuizPayload, updateQuiz, UpdateQuizPayload, validateQuizTitle } from "../../fetch/quiz";

export enum QuizSuccessStates {
  update = 'UPDATE',
  update_published = 'UPDATE_PUBLISHED',
  update_unpublished = 'UPDATE_UNPUBLISHED',
  delete = 'DELETE',
  create = 'CREATE',
  reorder = 'REORDER',
  question_created = 'QUESTION_CREATED',
  question_updated = 'QUESTION_UPDATED',
  question_deleted = 'QUESTION_DELETED',
  question_added_from_library = 'QUESTION_ADDED_FROM_LIBRARY',
  update_assessment_mode = 'UPDATE_ASSESSMENT_MODE',
  note_created = 'NOTE_CREATED',
  note_deleted = 'NOTE_DELETED',
  note_updated = 'NOTE_UPDATED'
}

export const SUCCESS_MESSAGES = {
  [QuizSuccessStates.update]: "success_messages.quiz_updated",
  [QuizSuccessStates.update_published]: "success_messages.quiz_published",
  [QuizSuccessStates.update_unpublished]: "success_messages.quiz_unpublished",
  [QuizSuccessStates.reorder]: "success_messages.quiz_order_updated",
  [QuizSuccessStates.delete]: "success_messages.quiz_deleted",
  [QuizSuccessStates.create]: "success_messages.quiz_created",
  [QuizSuccessStates.question_created]: "success_messages.question_created",
  [QuizSuccessStates.question_updated]: "success_messages.question_updated",
  [QuizSuccessStates.question_deleted]: "success_messages.question_deleted",
  [QuizSuccessStates.question_added_from_library]: "success_messages.question_added",
  [QuizSuccessStates.update_assessment_mode]: "success_messages.quiz_assessment_mode_updated",
  [QuizSuccessStates.note_created]: "success_messages.note_created",
  [QuizSuccessStates.note_deleted]: "success_messages.note_deleted",
  [QuizSuccessStates.note_updated]: "success_messages.note_updated",
};

export interface QuizQuestion {
  entityType: 'question'
  entityId: number
  position: number
  question: {
    id: string
    name: string
    isPhising?: number | boolean
    apps?: {
      id: number
      name: string
      type: string
    }[]
  }
}

export interface QuizNoteItem {
  entityType: 'note'
  entityId: number
  position: number
  note: {
    id: string
    name: string
    content: string
  }
}

export type QuizViewItem = QuizQuestion | QuizNoteItem

export interface Quiz {
  id: number;
  title: string;
  published: boolean;
  quizQuestions?: QuizViewItem[]
  updatedAt: string
  hash?: string;
  visibility?: string
  hasResults?: boolean
  assessmentMode?: boolean
}

export interface QuizSlice {
  quizzes: Quiz[] | []
  fetchQuizzes: () => Promise<void>,
  updateQuiz: (data: UpdateQuizPayload, successAction?: string) => void,
  reorderQuiz: (data: ReorderQuizPayload) => void
  deleteQuiz: (id: number) => void,
  validateQuizName: (title: string) => Promise<void>,
  createQuiz: (title: string, visibility: string) => Promise<number>,
  quizActionSuccess: null | QuizSuccessStates,
  cleanQuizActionSuccess: () => void
  setQuizActionSuccess: (successState: string) => void,
  cleanQuizzes: () => void
}

export const createQuizSlice: StateCreator<
  QuizSlice,
  [],
  [],
  QuizSlice
> = (set) => ({
  quizzes: [],
  quizActionSuccess: null,

  cleanQuizActionSuccess: async () => {
    set({ quizActionSuccess: null })
  },

  fetchQuizzes: async () => {
    const res = await getQuizzes()
    set({
      quizzes: res,
    })
  },

  updateQuiz: async (toUpdate: UpdateQuizPayload, successAction?: string) => {
    set({ quizActionSuccess: null })
    await updateQuiz(toUpdate)

    // TODO refactor error/success handling
    if (successAction) {
      set({ quizActionSuccess: QuizSuccessStates[successAction] })
    } else {
      set({ quizActionSuccess: QuizSuccessStates.update })
    }
  },

  reorderQuiz: async (reorderData: ReorderQuizPayload) => {
    set({ quizActionSuccess: null })
    await reorderQuiz(reorderData)

    set({
      quizActionSuccess: QuizSuccessStates.reorder
    })
  },

  deleteQuiz: async (id: number) => {
    set({ quizActionSuccess: null })
    await deleteQuiz(id)

    // TODO until we have a seamless loading state we should make fetch quizzes as soon as posible here
    const quizzes = await getQuizzes()

    set({
      quizActionSuccess: QuizSuccessStates.delete,
      quizzes: quizzes
    })
  },

  validateQuizName: async (title: string) => {
    await validateQuizTitle(title)
  },

  createQuiz: async (title: string, visibility: string) => {
    set({ quizActionSuccess: null })
    const quizId = await createQuiz(title, visibility)

    set({
      quizActionSuccess: QuizSuccessStates.create
    })

    return quizId
  },

  setQuizActionSuccess: async (successState: QuizSuccessStates) => {
    set({ quizActionSuccess: successState })
  },

  cleanQuizzes: () => {
    set({ quizzes: [] })
  }
})
